import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useToast,
  Spinner,
  Center,
  VStack,
  HStack,
  Badge,
  Icon
} from '@chakra-ui/react';
import { FiMail, FiUsers, FiClock, FiCheckCircle } from 'react-icons/fi';
import api from '../../../../services/api';
import ForwardingHistory from './components/ForwardingHistory';
import TeamPerformance from './components/TeamPerformance';
import ForwardingAnalytics from './components/ForwardingAnalytics';

const ForwardingControlPanel = () => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchStatistics();
    const interval = setInterval(fetchStatistics, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchStatistics = async () => {
    try {
      const [statusRes, detailedRes] = await Promise.all([
        api.get('/email-forwarding/status'),
        api.get('/email-forwarding/statistics/detailed')
      ]);

      setStatistics(statusRes.data.forwarding);
      setDetailedStats(detailedRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast({
        title: 'Erro ao carregar estatísticas',
        description: error.response?.data?.message || 'Erro ao buscar dados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
    }
  };

  const calculateResponseRate = () => {
    if (!detailedStats?.overall) return 0;
    const { total, replied } = detailedStats.overall;
    if (total === 0) return 0;
    return ((replied / total) * 100).toFixed(1);
  };

  const formatTime = (ms) => {
    if (!ms) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Container maxW="8xl" py={5}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <Heading size="lg">Painel de Controle - Encaminhamento de Emails</Heading>
          <Badge 
            colorScheme={statistics?.enabled ? 'green' : 'red'}
            fontSize="md"
            px={3}
            py={1}
          >
            {statistics?.enabled ? 'ATIVO' : 'INATIVO'}
          </Badge>
        </HStack>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5}>
          <Stat
            bg="white"
            p={5}
            borderRadius="lg"
            boxShadow="sm"
            borderLeft="4px solid"
            borderLeftColor="blue.500"
          >
            <StatLabel display="flex" alignItems="center">
              <Icon as={FiMail} mr={2} />
              Total Encaminhados
            </StatLabel>
            <StatNumber fontSize="3xl">
              {statistics?.totalEmailsForwarded || 0}
            </StatNumber>
            <StatHelpText>
              {statistics?.statistics?.successfulForwards || 0} com sucesso
            </StatHelpText>
          </Stat>

          <Stat
            bg="white"
            p={5}
            borderRadius="lg"
            boxShadow="sm"
            borderLeft="4px solid"
            borderLeftColor="green.500"
          >
            <StatLabel display="flex" alignItems="center">
              <Icon as={FiCheckCircle} mr={2} />
              Taxa de Resposta
            </StatLabel>
            <StatNumber fontSize="3xl">
              {calculateResponseRate()}%
            </StatNumber>
            <StatHelpText>
              {detailedStats?.overall?.replied || 0} respondidos
            </StatHelpText>
          </Stat>

          <Stat
            bg="white"
            p={5}
            borderRadius="lg"
            boxShadow="sm"
            borderLeft="4px solid"
            borderLeftColor="purple.500"
          >
            <StatLabel display="flex" alignItems="center">
              <Icon as={FiClock} mr={2} />
              Tempo Médio de Resposta
            </StatLabel>
            <StatNumber fontSize="2xl">
              {formatTime(detailedStats?.overall?.avgResponseTime)}
            </StatNumber>
            <StatHelpText>
              {detailedStats?.overall?.pending || 0} pendentes
            </StatHelpText>
          </Stat>

          <Stat
            bg="white"
            p={5}
            borderRadius="lg"
            boxShadow="sm"
            borderLeft="4px solid"
            borderLeftColor="orange.500"
          >
            <StatLabel display="flex" alignItems="center">
              <Icon as={FiUsers} mr={2} />
              Destinatários
            </StatLabel>
            <StatNumber fontSize="3xl">
              {statistics?.recipients || 0}
            </StatNumber>
            <StatHelpText>
              Ciclo #{statistics?.recipientCycle?.cycleNumber || 1}
            </StatHelpText>
          </Stat>
        </SimpleGrid>

        {/* Main Tabs */}
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={5}>
          <Tabs colorScheme="blue">
            <TabList>
              <Tab>Histórico de Encaminhamentos</Tab>
              <Tab>Desempenho da Equipe</Tab>
              <Tab>Análise e Estatísticas</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <ForwardingHistory />
              </TabPanel>
              <TabPanel>
                <TeamPerformance 
                  teamData={detailedStats?.teamPerformance || []}
                  onRefresh={fetchStatistics}
                />
              </TabPanel>
              <TabPanel>
                <ForwardingAnalytics 
                  stats={detailedStats}
                  onRefresh={fetchStatistics}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
    </Container>
  );
};

export default ForwardingControlPanel;